<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { FormCreateOrg } from '@/components/forms'
import { Spinner } from '@/components/ui/spinner'
import { ChevronRight } from 'lucide-vue-next'

const store = useStore()
const router = useRouter()

const firebaseUser = computed(() => store.getters['user/getUser'])
const member = computed(() => store.getters['user/getMember'])
const organizations = computed(() => store.getters['org/getOrgs'])
const projectInvites = computed(() => store.getters['project/getAllInvites'])
const orgInvites = computed(() => store.getters['org/getPendingInvites'])

const allInvites = computed(() => {
  const project = projectInvites.value.map(i => ({
    key: `project-${i.uuid}`,
    type: 'project',
    typeLabel: 'Project',
    name: i.project_name,
    raw: i
  }))
  const org = orgInvites.value.map(i => ({
    key: `org-${i.id}`,
    type: 'org',
    typeLabel: 'Organization',
    name: i.organization_name,
    raw: i
  }))
  return [...project, ...org]
})
const isLoading = ref(false)

const userAvatar = computed(() => member.value?.avatar_url || firebaseUser.value?.photoURL || null)
const userName = computed(() => member.value?.display_name || firebaseUser.value?.displayName || null)
const userEmail = computed(() => member.value?.email || firebaseUser.value?.email || 'Error')
const userInitial = computed(() => {
  const name = userName.value
  const email = userEmail.value

  if (name) {
    return name.charAt(0).toUpperCase()
  } else if (email) {
    return email.charAt(0).toUpperCase()
  }
  return 'V'
})

const firebaseToken = computed(() => store.getters.getFirebaseToken)

const isFirebaseReady = computed(() => {
  return firebaseUser.value !== null && firebaseToken.value !== null
})

const showCreateOrgForm = ref(false)
const displayOrganizations = computed(() => {
  return organizations.value.map(org => ({
    id: org.organization_id,
    name: org.organization_name,
    avatar: org.avatar_url || '🏢',
    role: org.current_member?.role
  }))
})

const handleCreateOrganization = () => {
  showCreateOrgForm.value = true
}

const handleFormSubmit = async (result) => {
  if (result.success) {
    showCreateOrgForm.value = false

    try {
      await store.dispatch('org/fetchOrgs')

      if (organizations.value.length > 0) {
        const firstOrg = organizations.value[0]
        router.push({ name: 'Organization', params: { org_id: firstOrg.organization_id } })
      } else {
        router.push({ name: 'Dashboard' })
      }
    } catch (error) {
      console.error('Failed to fetch organizations after creation:', error)
      showCreateOrgForm.value = false
    }
  }
}

onMounted(async () => {
  const waitForFirebase = () => {
    return new Promise((resolve) => {
      const checkFirebase = () => {
        if (isFirebaseReady.value) {
          resolve()
        } else {
          setTimeout(checkFirebase, 100)
        }
      }
      checkFirebase()
    })
  }

  isLoading.value = true
  try {
    await waitForFirebase()
    await store.dispatch('org/fetchOrgs')
    await store.dispatch('org/fetchPendingOrgInvites')
    await store.dispatch('project/fetchAllInvitesFirebase', { firebaseToken: firebaseToken.value })
  } catch (error) {
    console.error('Failed to fetch data:', error)
  } finally {
    isLoading.value = false
  }
})


const handleCloseForm = () => {
  showCreateOrgForm.value = false
}

const handleJoinInvite = async (item) => {
  if (item.type === 'org') {
    await store.dispatch('org/acceptOrgInvite', item.raw.id)
    await store.dispatch('org/fetchPendingOrgInvites')
  } else {
    await store.dispatch('project/acceptInviteFirebase', {
      invite: item.raw,
      firebaseToken: firebaseToken.value
    })
    await store.dispatch('project/fetchAllInvitesFirebase', { firebaseToken: firebaseToken.value })
  }
  await store.dispatch('org/fetchOrgs')
}

const handleOpenOrganization = (org) => {
  store.dispatch('org/setSelectedOrg', org.id.toString())
  router.push({ name: 'Organization', params: { org_id: org.id } })
}

const handleLogout = async () => {
  try {
    await store.dispatch('logout')
    router.push({ name: 'AuthLogin' })
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

const handleHome = () => {
  router.push({ name: 'Dashboard' })
}
</script>

<template>
  <div class="p-3 mx-auto">
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center">
        <Avatar size="base" class="mr-3 bg-primary">
          <AvatarImage v-if="userAvatar" :src="userAvatar" alt="User Avatar" />
          <AvatarFallback class="text-white font-bold">{{ userInitial }}</AvatarFallback>
        </Avatar>
        <div class="text-muted-foreground text-sm">
          <div v-if="userName || userEmail">
            <strong v-if="userName" class="text-foreground">{{ userName }}</strong>
          </div>
          <div>
            <span class="text-muted-foreground">{{ userEmail }}</span>
            <a href="#" @click.prevent="handleLogout" class="text-primary hover:underline"> Change</a>
          </div>
        </div>
      </div>
    </div>

    <FormCreateOrg
      :open="showCreateOrgForm"
      @close="handleCloseForm"
      @submit="handleFormSubmit"
    />

    <div v-if="isLoading || !isFirebaseReady" class="flex justify-center py-8">
      <Spinner class="w-6 h-6" />
      <span class="ml-2 text-muted-foreground">
        {{ !isFirebaseReady ? 'Initializing...' : 'Loading...' }}
      </span>
    </div>

    <template v-else>
      <div class="mb-8">
        <h2 class="text-xl font-semibold text-foreground mb-3">Invitations</h2>
        <div v-if="allInvites.length > 0" class="border border-border rounded-lg max-h-[300px] overflow-y-auto">
          <div
            v-for="item in allInvites"
            :key="item.key"
            @click="handleJoinInvite(item)"
            class="flex items-center justify-between p-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 transition-colors"
          >
            <div class="text-sm">
              <h3 class="text-foreground">{{ item.name }}</h3>
              <p class="text-muted-foreground">{{ item.typeLabel }}</p>
            </div>
            <div>
              <Button variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground">
                <span>Join</span>
                <ChevronRight class="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-muted-foreground">No pending invitations</p>
      </div>

      <div class="mb-8">
        <h2 class="text-xl font-semibold text-foreground mb-3">Available organizations</h2>
        <div v-if="displayOrganizations.length > 0" class="border border-border rounded-lg max-h-[300px] overflow-y-auto">
          <div
            v-for="org in displayOrganizations"
            :key="org.id"
            @click="handleOpenOrganization(org)"
            class="flex items-center justify-between p-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 transition-colors"
          >
            <div class="text-sm">
              <h3 class="text-foreground">{{ org.name }}</h3>
            </div>
            <div>
              <Button variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground">
                <span>Open</span>
                <ChevronRight class="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-muted-foreground">No organizations yet</p>
      </div>
    </template>

    <div class="mt-8 text-center">
      <p class="text-sm text-muted-foreground mb-4">
        Not seeing your organization? <br>
        <a href="#" @click.prevent="handleLogout" class="text-primary hover:underline">Try a Different Account</a>
        or
        <a href="#" @click.prevent="handleCreateOrganization" class="text-primary hover:underline">Create a new one</a>
      </p>

      <Button variant="outline" @click="handleLogout" class="px-6 py-2">
        Logout
      </Button>
    </div>
  </div>
</template>
